import {
  TActivity,
  TNetworkViewData,
  TProject,
  TSwitch,
  TTrack,
} from './project.type'

export type EShift = 'day' | 'night' | 'both'

export type TClientInfo = {
  id: string
  name: string
  logo: string
  email: string
}
export type TTeamInfo = {
  id: string
  name: string
  clientId: string
  color: string
  acronym: string
  isActive: boolean
}

export type TProjectInfo = {
  id: string
  name: string
  acronym: string
  title: string
  day_start: string
  day_end: string
  night_start: string
  night_end: string
  submit_day: string
  submit_time: string
  status: string
  echantillonValue: number
  echantillon_acronyme: string
  kmStart: number
  kmEnd: number
}

export type TSecteurInfo = {
  id: string
  project: string
  name: string
  acronym: string
  kmStart: number
  kmEnd: number
}

export type TSubSecteurInfo = {
  id: string
  project: string
  name: string
  kmStart: number
  kmEnd: number
}

export type TVoieInfo = {
  id: string
  project: string
  name: string
  acronyme: string
}

export type TAiguillageInfo = {
  id: string
  project: string
  type: number
  kmPoint: number
  isActive: boolean
}

export type TActivityInfo = {
  id: string
  project: string
  week: string
  requestorName: string
  phoneNumber: string
  holderPhoneNumber: string
  holderName: string
  voie: string
  kmStart: number
  kmEnd: number
  team: TTeamInfo
  company: string
  shift: EShift
  date: string
  status: string
  comment?: string
  updatedAt: Date
  createdAt: Date
  docNumber: string
  licenseNumber: string
}

export type TProjectWithInfos = TProject & {
  tracks: TTrack[]
  switches: TSwitch[]
  activities: TActivity[]
}

export type TInfoByWeek = {
  approved: boolean
  id: string
  day: string
  night: string
  date: string
  activities: TActivity[]
  movements: TActivity[]
  // Network-native (Lot 3, Unit 2): the info object no longer carries a full
  // project. Consumers read network/PK scope; project-scoped lookups (permission,
  // cross-project conflicts) moved to client/network scope.
  projectInfo: TNetworkViewData
}

export type TPointAccessInfo = {
  id: string
  project: string
  type: number
  name: string
  voie: string[]
  kmPoint: number
  isActive: boolean
}

export type TProjectWithAllDetails = {
  id: string
  voie: TVoieInfo[]
  aiguillage: TAiguillageInfo[]
  secteur: TSecteurInfo[]
  subSecteur: TSubSecteurInfo[]
  activities: TActivityInfo[]
  teams?: TTeamInfo[]
  projectInfo: TProjectInfo
  pointsAccess: TPointAccessInfo[]
}

export type SecteurDetailType = {
  name: string
}

export type InfoGlobalFormType = {
  author: string
  telAuthor: string
  updateDate: string
  informationText: string
  secteurList?: SecteurDetailType[]
}

export type TPopupGestion = {
  data: any
  initElement: any
  columns: any
  titlePopup: string
  iconPopup: string
  titleTable: string
  textButtonAdd: string
  noRowsText: string
  hasActionDelete: boolean
  handleChangeSelected?: any
}

export enum EErrorCodes {
  ZOD_ERROR = 'ZOD_ERROR',
  NONE = 'NONE',
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  NOT_FOUND = 'NOT_FOUND',
  BAD_REQUEST = 'BAD_REQUEST',
  INTERNAL_SERVER_ERROR = 'INTERNAL_SERVER_ERROR',
  CONFLICT = 'CONFLICT',
  UNPROCESSABLE_ENTITY = 'UNPROCESSABLE_ENTITY',
  TOO_MANY_REQUESTS = 'TOO_MANY_REQUESTS',
  PASSWORDS_DO_NOT_MATCH = 'PASSWORDS_DO_NOT_MATCH',
  INVALID_CREDENTIALS = 'INVALID_CREDENTIALS',
  MISSING_TOKEN = 'MISSING_TOKEN',
  USER_BANNED = 'USER_BANNED',
  EXPIRED_TOKEN = 'EXPIRED_TOKEN',
  INVALID_TOKEN = 'INVALID_TOKEN',
  RESET_PASSWORD_TOO_MANY = 'RESET_PASSWORD_TOO_MANY',
  INVALID_RESOURCE = 'INVALID_RESOURCE',
  INVALID_PHOTO_TYPE = 'INVALID_PHOTO_TYPE',
  INVALID_MEDIA_TYPE = 'INVALID_MEDIA_TYPE',
  INVALID_FILTER_QUERY = 'INVALID_FILTER_QUERY',
  PROJECT_HAS_JUNCTIONS = 'PROJECT_HAS_JUNCTIONS',
  NETWORK_WRONG_CLIENT = 'NETWORK_WRONG_CLIENT',
  NETWORK_ERROR = 'NETWORK_ERROR',
  ACTIVITY_GROUP_DATE_TAKEN = 'ACTIVITY_GROUP_DATE_TAKEN',
  PK_NOT_ON_SEGMENT = 'PK_NOT_ON_SEGMENT',
  TASK_NOT_IN_JOB = 'TASK_NOT_IN_JOB',
  TASKS_ON_GROUPED_ACTIVITY = 'TASKS_ON_GROUPED_ACTIVITY',
  ACTIVITY_HAS_REPORT = 'ACTIVITY_HAS_REPORT',
  PROTECTION_ZONE_MEMBER_TYPE_MISMATCH = 'PROTECTION_ZONE_MEMBER_TYPE_MISMATCH',
  PROTECTION_ZONE_CYCLE = 'PROTECTION_ZONE_CYCLE',
  PROTECTION_ZONE_IN_GROUP = 'PROTECTION_ZONE_IN_GROUP',
  PROTECTION_ZONE_SEGMENTS_OR_MEMBERS = 'PROTECTION_ZONE_SEGMENTS_OR_MEMBERS',
}

// Codes that mean the session is genuinely invalid and require a logout.
// Anything else (network, 5xx, unknown) must NOT trigger a logout.
export const AUTH_ERROR_CODES: ReadonlySet<EErrorCodes> = new Set([
  EErrorCodes.INVALID_TOKEN,
  EErrorCodes.EXPIRED_TOKEN,
  EErrorCodes.MISSING_TOKEN,
  EErrorCodes.UNAUTHORIZED,
])

export const isAuthError = (err: unknown): boolean =>
  err instanceof ErrorHelper && AUTH_ERROR_CODES.has(err.code)

export const isNetworkError = (err: unknown): boolean =>
  err instanceof ErrorHelper && err.code === EErrorCodes.NETWORK_ERROR

export type TFetchResponse<T> = {
  status: number
  ok: boolean
  statusText: string
  data?: T
}

export class ErrorHelper extends Error {
  statusCode: number
  code: EErrorCodes
  data: any
  error: string

  constructor({statusCode, message, code, data, error}: TError) {
    super(message)
    this.statusCode = statusCode
    this.code = code
    this.data = data
    this.error = error
  }
}

export type TError = {
  statusCode: number
  message: string
  code: EErrorCodes
  data?: any
  error: string
}
